import React, { useState, useEffect, useRef } from 'react';
import { Bot, Check, Phone, Sparkles, Layout, Workflow, MessageSquare } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderBadge from '@/components/editorial/AnimatedBorderBadge';

const EditorialAnalyzeSection: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isVisible, setIsVisible] = useState(false);
  const [stats, setStats] = useState({ calls: 0, leads: 0, appointments: 0 });
  const [showAgents, setShowAgents] = useState([false, false, false]);
  const [agentStatuses, setAgentStatuses] = useState(['Available', 'Available', 'Available']);
  const [showInsight, setShowInsight] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const features = [
    { text: "Voice & Conversation AI", icon: Phone },
    { text: "AI Content Creation", icon: Sparkles },
    { text: "AI Funnel & Page Builder", icon: Layout },
    { text: "Intelligent Workflow Builder", icon: Workflow },
    { text: "AI Review Responder", icon: MessageSquare }
  ];

  const agents = [
    { name: 'Sales Agent', avatar: '🤖', calls: 23 },
    { name: 'Support Agent', avatar: '🎧', calls: 45 },
    { name: 'Booking Agent', avatar: '📅', calls: 12 }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    // Animate stats
    const duration = 1500;
    const steps = 30;
    let step = 0;
    
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setStats({
        calls: Math.round(847 * progress),
        leads: Math.round(234 * progress),
        appointments: Math.round(89 * progress)
      });
      if (step >= steps) clearInterval(timer);
    }, duration / steps);

    // Show agents staggered
    setTimeout(() => setShowAgents([true, false, false]), 600);
    setTimeout(() => setShowAgents([true, true, false]), 900);
    setTimeout(() => setShowAgents([true, true, true]), 1200);

    // Animate agent statuses
    setTimeout(() => setAgentStatuses(['On Call', 'Available', 'Available']), 1500);
    setTimeout(() => setAgentStatuses(['On Call', 'Available', 'Processing']), 2000);
    setTimeout(() => setAgentStatuses(['On Call', 'On Call', 'Processing']), 2500);

    // Show insight
    setTimeout(() => setShowInsight(true), 2000);

    return () => clearInterval(timer);
  }, [isVisible]);

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'On Call':
        return { bg: '#3B82F620', color: '#3B82F6' };
      case 'Available':
        return { bg: '#10B98120', color: '#10B981' };
      case 'Processing':
        return { bg: '#F59E0B20', color: '#F59E0B' };
      default:
        return { bg: '#10B98120', color: '#10B981' };
    }
  };

  return (
    <section ref={sectionRef} className="py-16 px-[7%] sm:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            {/* Badge with animated border */}
            <AnimatedBorderBadge text="AI Agents" icon={Bot} align="left" />

            <h2 
              className="text-3xl lg:text-4xl font-bold tracking-tight mb-4"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              Your Always On AI Assistants.
            </h2>

            <p 
              className="text-sm mb-8"
              style={{ color: isDark ? '#AAAAAA' : '#555555' }}
            >
              Your AI Employees never take PTO or get sick. No tax, no pensions, just results.
            </p>

            {/* Feature checklist */}
            <div className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.5s ease-out ${index * 100}ms`
                  }}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                    <Check className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  </div>
                  <span className="text-sm" style={{ color: isDark ? '#CCCCCC' : '#333333' }}>{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <button 
              onClick={() => document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98] hover:opacity-90"
              style={{
                backgroundColor: isDark ? '#FFFFFF' : '#000000',
                color: isDark ? '#000000' : '#FFFFFF',
              }}
            >
              Meet Your AI Team →
            </button>
          </div>

          {/* Right Visual - AI Agents Dashboard */}
          <div>
            <div 
              className="rounded-xl overflow-hidden"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#F8F8F8', 
                border: isDark ? '1px solid #333333' : '1px solid #E5E5E5',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(-4px) scale(1)' : 'translateY(20px) scale(0.95)',
                boxShadow: isVisible ? (isDark ? '0 20px 40px -10px rgba(0, 0, 0, 0.5)' : '0 20px 40px -10px rgba(0, 0, 0, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.1)') : 'none',
                transition: 'all 0.6s ease-out'
              }}
            >
              {/* Dashboard header */}
              <div className="p-4 border-b" style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bot className="w-4 h-4" style={{ color: isDark ? '#AAAAAA' : '#555555' }} />
                    <span className="text-sm font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>AI Agents</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div 
                      className={`w-2 h-2 rounded-full ${isDark ? 'bg-emerald-400' : 'bg-emerald-500'}`}
                      style={{ animation: isVisible ? 'pulse 1s ease-in-out infinite' : 'none' }}
                    />
                    <span className={`text-xs font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>3 Active</span>
                  </div>
                </div>
              </div>

              {/* Agent Stats with animated counters */}
              <div className="p-4">
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    { label: 'Calls Handled', value: stats.calls, change: '+12%' },
                    { label: 'Leads Qualified', value: stats.leads, change: '+28%' },
                    { label: 'Appointments Set', value: stats.appointments, change: '+34%' }
                  ].map((stat, index) => (
                    <div 
                      key={index}
                      className="rounded-lg p-3 relative overflow-hidden"
                      style={{ 
                        backgroundColor: isDark ? '#222222' : '#FFFFFF', 
                        border: isDark ? '1px solid #333333' : '1px solid #E0E0E0',
                        opacity: isVisible ? 1 : 0,
                        transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
                        transition: `all 0.4s ease-out ${300 + index * 100}ms`
                      }}
                    >
                      {/* Subtle shimmer */}
                      <div 
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: 'linear-gradient(90deg, transparent 0%, rgba(16, 185, 129, 0.05) 50%, transparent 100%)',
                          animation: isVisible ? `shimmer 2s ease-in-out ${index * 300}ms infinite` : 'none'
                        }}
                      />
                      <span className="text-[10px] font-medium relative z-10" style={{ color: isDark ? '#666666' : '#888888' }}>{stat.label}</span>
                      <div className="flex items-end justify-between mt-1 relative z-10">
                        <span className="text-xl font-bold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>{stat.value}</span>
                        <span 
                          className={`text-[10px] font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
                          style={{
                            animation: isVisible ? `flashIn 0.5s ease-out ${1500 + index * 200}ms both` : 'none'
                          }}
                        >
                          {stat.change}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Active Agents with status changes */}
                <div className="space-y-2">
                  {agents.map((agent, index) => {
                    const statusStyle = getStatusStyle(agentStatuses[index]);
                    return (
                      <div 
                        key={index}
                        className="rounded-lg p-3 flex items-center justify-between"
                        style={{ 
                          backgroundColor: isDark ? '#222222' : '#FFFFFF', 
                          border: isDark ? '1px solid #333333' : '1px solid #E0E0E0',
                          opacity: showAgents[index] ? 1 : 0,
                          transform: showAgents[index] ? 'translateX(0)' : 'translateX(-20px)',
                          transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <span 
                            className="text-lg"
                            style={{
                              animation: showAgents[index] ? 'bounceIn 0.5s ease-out' : 'none'
                            }}
                          >
                            {agent.avatar}
                          </span>
                          <div>
                            <div className="text-xs font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>{agent.name}</div>
                            <div className="text-[10px]" style={{ color: isDark ? '#888888' : '#666666' }}>{agent.calls} calls today</div>
                          </div>
                        </div>
                        <span 
                          className="px-2 py-0.5 rounded text-[10px] font-medium transition-all duration-300"
                          style={{ 
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color,
                            animation: agentStatuses[index] === 'On Call' ? 'pulse 1.5s ease-in-out infinite' : 'none'
                          }}
                        >
                          {agentStatuses[index]}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Insight */}
                <div 
                  className="mt-3 p-3 rounded-lg" 
                  style={{ 
                    backgroundColor: isDark ? '#1e1b4b' : '#EEF2FF', 
                    border: isDark ? '1px solid #4338ca' : '1px solid #C7D2FE',
                    opacity: showInsight ? 1 : 0,
                    transform: showInsight ? 'translateY(0)' : 'translateY(10px)',
                    transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Sparkles 
                      className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}
                      style={{ animation: showInsight ? 'sparkle 1.5s ease-in-out infinite' : 'none' }}
                    />
                    <span className={`text-xs font-medium ${isDark ? 'text-indigo-300' : 'text-indigo-800'}`}>AI saved 127 hours of manual work this week</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

export default EditorialAnalyzeSection;
